<template>
  <t-popup
    :visible="visible"
    placement="bottom-left"
    :hide-empty-popup="true"
    :show-arrow="false"
    overlay-class-name="variable-selector-popup"
    @visibleChange="handleVisibleChange"
  >
    <template #default>
      <slot>
        <div
          class="variable-selector-trigger"
          size="small"
          @click="visible = true"
        >
          <span>{{
            selectedVariableName ||
            t(type == JudgeType.INFO ? t("选择信息") : t("选择变量"))
          }}</span>
          <img :src="arrow" />
        </div>
      </slot>
    </template>

    <template #content>
      <t-cascader-panel
        v-model="selectedValue"
        class="variable-selector-panel"
        :options="options"
        :placeholder="t(type == JudgeType.INFO ? t('选择信息') : t('选择变量'))"
        :on-change="handleChange"
        :empty="t('暂无数据')"
        :check-strictly="false"
        trigger="hover"
      >
        <template #option="{ item }">
          <div
            v-if="item.value === 'ADD_BUTTON'"
            class="variable-selector-option-add-button"
            @click="
              handleButtonClick(
                { option: item, type: 'create', index: item.childIndex },
                $event,
              )
            "
          >
            {{ t("新建变量") }}
          </div>
          <div
            v-else
            class="variable-selector-option"
            @mouseenter="changeButtonVisible(item, true)"
            @mouseleave="changeButtonVisible(item, false)"
          >
            <span class="variable-selector-option-label">{{ item.label }}</span>
            <div v-if="item.showButton" class="variable-selector-option-button">
              <EditIcon
                @click="
                  ({ e }) =>
                    handleButtonClick(
                      { option: item, index: item.childIndex, type: 'edit' },
                      e,
                    )
                "
              />
              <DeleteIcon
                @click="
                  ({ e }) =>
                    handleButtonClick(
                      { option: item, index: item.childIndex, type: 'delete' },
                      e,
                    )
                "
              />
            </div>
          </div>
        </template>
      </t-cascader-panel>
    </template>
  </t-popup>
</template>

<script lang="ts" setup>
import { ref, computed, watch } from "vue";
import { t } from "@/utils/i18n";
import arrow from "./img/arrow.svg";
import { JudgeType } from "@/types/conditionJudgeNodeType";
import { EditIcon, DeleteIcon } from "tdesign-icons-vue-next";

// 类型定义优化
interface Variable {
  id: string;
  label: string;
  type: JudgeType;
  children?: Variable[];
  value: string | JudgeType;
}

// Props 类型定义
interface Props {
  type: JudgeType;
  defaultValue: string;
  variables?: Variable[];
  placeholder?: string;
}

const props = withDefaults(defineProps<Props>(), {
  placeholder: "",
  variables: () => [],
});

// Emits 类型定义
const emit = defineEmits<{
  (
    e: "click-button",
    variable: { option: Variable; type: string; index: number },
  ): void;
  (e: "select", value: string): void;
  (e: "visible-change", visible: boolean): void;
}>();

// 响应式状态
const selectedValue = ref<string>(props.defaultValue);
const visible = ref<boolean>(false);

// 优化计算属性
const options = computed(() => {
  // AI智能判断模式：只显示系统变量、信息收集节点、接口调用节点
  // 过滤掉自定义变量组（value === JudgeType.CUSTOM 且没有 children 的项）
  return props.variables.filter((v) => {
    // 排除自定义变量组（空的自定义变量组）
    if (
      v.value === JudgeType.CUSTOM &&
      (!v.children || v.children.length === 0)
    ) {
      return false;
    }
    return true;
  });
});

const changeButtonVisible = (item: any, visible: boolean) => {
  if (item.isCustom) {
    item.showButton = visible;
  }
};

const handleButtonClick = ({ option, type, index }, e) => {
  e.preventDefault();
  e.stopPropagation();
  visible.value = false;
  emit("click-button", {
    option,
    type,
    index,
  });
};

const selectedVariableName = computed(() => {
  const firstOption = options.value[0];
  if (!firstOption?.children) return "";

  const selectedLabel = options.value
    .map((v) => v.children)
    .flat()
    .find((v) => v?.value === selectedValue.value)?.label;
  return selectedLabel;
});

// 处理选择变化
const handleChange = (value: string): void => {
  if (!value) return;

  visible.value = false;
  emit("select", value);
};

// 处理可见性变化
const handleVisibleChange = (_: boolean, ctx: any) => {
  if (ctx.trigger === "document") {
    // 点击外部触发
    visible.value = false;
  }
};

// 监听可见性变化
watch(visible, (newValue) => {
  emit("visible-change", newValue);
});
</script>

<style lang="less" scoped>
.variable-selector-trigger {
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 5px 10px;
  height: 23px;
  border-radius: 3px;
  margin: 2px;
  color: var(--primary-color, #1c66e5);
  background: var(--primary-bg-color, #e3eeff);

  &:hover {
    opacity: 0.9;
  }

  span {
    font-size: 14px;
    line-height: 14px;
    text-align: center;
  }
}

.variable-selector-panel {
  :deep(.t-cascader__menu) {
    margin: 0;
  }

  .variable-selector-option-add-button {
    height: 23px;
    border-radius: 3px;
    margin: 2px;
    color: var(--primary-color, #1c66e5);
  }

  :deep(.t-is-disabled) {
    cursor: pointer;
  }

  .variable-selector-option {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    img {
      height: 16px;
    }
    .variable-selector-option-label {
      flex: 1;
      min-width: 30px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .variable-selector-option-button {
      display: flex;
      gap: 4px;
    }
  }

  // 限制级联选择器菜单项的文本长度
  :deep(.t-is-expanded) {
    .variable-selector-option {
      max-width: 90%;
    }
  }
}

.variable-selector-popup {
  padding: 0;
}
</style>
<!-- 全局样式，用于覆盖 t-popup 样式（仅作用于 variable-selector-popup） -->
<!-- 使用属性选择器避免被 replace-prefix 插件替换 -->
<!-- 注意：通过 .variable-selector-popup 作为父级类，限制样式作用范围 -->
<style lang="less">
// 重写 t-popup 样式
// 仅作用于 overlay-class-name 为 variable-selector-popup 的弹层
// 使用 [class~="t-popup"] 避免被 vite postcss replace-prefix 插件替换
.variable-selector-popup[class~="t-popup"] {
  font: var(--td-font-body-medium);
  box-sizing: border-box;
  margin: 0px;
  padding: 0px;
  list-style: none;
  color: var(--td-text-color-primary);
  display: inline-block;
  z-index: 5500;
}

// variable-selector-popup 的 content 样式
.variable-selector-popup [class~="t-popup__content"] {
  position: relative;
  background: var(--td-bg-color-container);
  box-shadow:
    var(--td-shadow-2), var(--td-shadow-inset-top),
    var(--td-shadow-inset-right), var(--td-shadow-inset-bottom),
    var(--td-shadow-inset-left);
  border-radius: var(--td-radius-medium);
  font-size: var(--td-font-size-body-medium);
  line-height: var(--td-line-height-body-medium);
  box-sizing: border-box;
  word-break: break-all;
  padding: var(--td-comp-paddingTB-xs) var(--td-comp-paddingLR-s);
}

.variable-selector-popup [class~="t-popup__arrow"] {
  position: absolute;
  z-index: 1;
  width: 8px;
  height: 8px;
}

.variable-selector-popup [class~="t-popup__arrow"]::before {
  position: absolute;
  content: "";
  width: 8px;
  height: 8px;
  transform: rotate(45deg);
  background: var(--td-bg-color-container);
}

// placement 相关样式
.variable-selector-popup[class~="t-popup"][data-popper-placement^="top"]
  [class~="t-popup__content"] {
  margin-bottom: var(--td-comp-margin-s);
}

.variable-selector-popup[class~="t-popup"][data-popper-placement^="top"]
  [class~="t-popup__content--arrow"] {
  margin-bottom: var(--td-comp-margin-l);
}

.variable-selector-popup[class~="t-popup"][data-popper-placement^="bottom"]
  [class~="t-popup__content"] {
  margin-top: var(--td-comp-margin-s);
}

.variable-selector-popup[class~="t-popup"][data-popper-placement^="left"]
  [class~="t-popup__content"] {
  margin-right: var(--td-comp-margin-s);
}

.variable-selector-popup[class~="t-popup"][data-popper-placement^="right"]
  [class~="t-popup__content"] {
  margin-left: var(--td-comp-margin-s);
}

.variable-selector-popup[class~="t-popup"][data-popper-placement^="bottom"]
  [class~="t-popup__content--arrow"] {
  margin-top: var(--td-comp-margin-l);
}

.variable-selector-popup[class~="t-popup"][data-popper-placement^="left"]
  [class~="t-popup__content"] {
  margin-right: var(--td-comp-margin-s);
}

.variable-selector-popup[class~="t-popup"][data-popper-placement^="left"]
  [class~="t-popup__content--arrow"] {
  margin-right: var(--td-comp-margin-l);
}

.variable-selector-popup[class~="t-popup"][data-popper-placement^="left"]
  [class~="t-popup__content--text"] {
  max-width: 480px;
}

.variable-selector-popup[class~="t-popup"][data-popper-placement^="right"]
  [class~="t-popup__content"] {
  margin-left: var(--td-comp-margin-s);
}

.variable-selector-popup[class~="t-popup"][data-popper-placement^="right"]
  [class~="t-popup__content--arrow"] {
  margin-left: var(--td-comp-margin-l);
}

.variable-selector-popup[class~="t-popup"][data-popper-placement^="right"]
  [class~="t-popup__content--text"] {
  max-width: 480px;
}

// 箭头位置样式
.variable-selector-popup[class~="t-popup"][data-popper-placement^="top"]
  [class~="t-popup__arrow"]::before {
  border-top-left-radius: 100%;
  box-shadow: var(--td-shadow-inset-left), var(--td-shadow-inset-bottom);
}

.variable-selector-popup[class~="t-popup"][data-popper-placement="top-start"]
  [class~="t-popup__arrow"] {
  left: 8px;
}

.variable-selector-popup[class~="t-popup"][data-popper-placement="top"]
  [class~="t-popup__arrow"] {
  left: 50%;
  margin-left: calc(-4px);
}

.variable-selector-popup[class~="t-popup"][data-popper-placement="top-end"]
  [class~="t-popup__arrow"] {
  left: calc(100% - 16px);
}

.variable-selector-popup[class~="t-popup"][data-popper-placement^="bottom"]
  [class~="t-popup__arrow"] {
  top: calc(-4px);
}

.variable-selector-popup[class~="t-popup"][data-popper-placement^="bottom"]
  [class~="t-popup__arrow"]::before {
  border-bottom-right-radius: 100%;
  box-shadow: var(--td-shadow-inset-top), var(--td-shadow-inset-right);
}

.variable-selector-popup[class~="t-popup"][data-popper-placement="bottom-start"]
  [class~="t-popup__arrow"] {
  left: 8px;
}

.variable-selector-popup[class~="t-popup"][data-popper-placement="bottom"]
  [class~="t-popup__arrow"] {
  left: 50%;
  margin-left: calc(-4px);
}

.variable-selector-popup[class~="t-popup"][data-popper-placement="bottom-end"]
  [class~="t-popup__arrow"] {
  left: calc(100% - 16px);
}

.variable-selector-popup[class~="t-popup"][data-popper-placement^="left"]
  [class~="t-popup__arrow"] {
  right: calc(-4px);
}

.variable-selector-popup[class~="t-popup"][data-popper-placement^="left"]
  [class~="t-popup__arrow"]::before {
  box-shadow: var(--td-shadow-inset-left), var(--td-shadow-inset-top);
}

.variable-selector-popup[class~="t-popup"][data-popper-placement="left-start"]
  [class~="t-popup__arrow"] {
  top: 8px;
}

.variable-selector-popup[class~="t-popup"][data-popper-placement="left"]
  [class~="t-popup__arrow"] {
  top: 50%;
  margin-top: calc(-4px);
}

.variable-selector-popup[class~="t-popup"][data-popper-placement="left-end"]
  [class~="t-popup__arrow"] {
  top: calc(100% - 16px);
}

.variable-selector-popup[class~="t-popup"][data-popper-placement^="right"]
  [class~="t-popup__arrow"] {
  left: calc(-4px);
}

.variable-selector-popup[class~="t-popup"][data-popper-placement^="right"]
  [class~="t-popup__arrow"]::before {
  box-shadow: var(--td-shadow-inset-right), var(--td-shadow-inset-bottom);
}

.variable-selector-popup[class~="t-popup"][data-popper-placement="right-start"]
  [class~="t-popup__arrow"] {
  top: 8px;
}

.variable-selector-popup[class~="t-popup"][data-popper-placement="right"]
  [class~="t-popup__arrow"] {
  top: 50%;
  margin-top: calc(-4px);
}

.variable-selector-popup[class~="t-popup"][data-popper-placement="right-end"]
  [class~="t-popup__arrow"] {
  top: calc(100% - 16px);
}

// 动画样式
.variable-selector-popup[class~="t-popup--animation-enter"],
.variable-selector-popup[class~="t-popup--animation-enter-from"],
.variable-selector-popup[class~="t-popup--animation-exiting"],
.variable-selector-popup[class~="t-popup--animation-leave-to"] {
  opacity: 0;
  visibility: hidden;
}

.variable-selector-popup[class~="t-popup--animation-enter-to"],
.variable-selector-popup[class~="t-popup--animation-entering"],
.variable-selector-popup[class~="t-popup--animation-leave-from"],
.variable-selector-popup[class~="t-popup--animation-leave"] {
  opacity: 1;
  visibility: visible;
  transform: none;
}

.variable-selector-popup[class~="t-popup--animation-enter-active"] {
  transition: opacity 0.2s linear;
}

.variable-selector-popup[class~="t-popup--animation-leave-active"] {
  transition:
    opacity 0.2s cubic-bezier(0, 0, 0.15, 1),
    visibility 0.2s cubic-bezier(0.82, 0, 1, 0.9);
}

.variable-selector-popup[class~="t-popup--animation-expand-enter-active"][data-popper-placement^="top"] {
  animation:
    0.2s cubic-bezier(0.38, 0, 0.24, 1) 0s 1 normal none running
      t-popup-animation-expand-in-top,
    0.2s linear 0s 1 normal none running t-fade-in;
}

.variable-selector-popup[class~="t-popup--animation-expand-leave-active"][data-popper-placement^="top"] {
  animation:
    0.2s cubic-bezier(0.38, 0, 0.24, 1) 0s 1 normal none running
      t-popup-animation-expand-out-top,
    0.2s cubic-bezier(0, 0, 0.15, 1) 0s 1 normal none running t-fade-out;
}

.variable-selector-popup[class~="t-popup--animation-expand-enter-active"][data-popper-placement^="bottom"] {
  animation:
    0.2s cubic-bezier(0.38, 0, 0.24, 1) 0s 1 normal none running
      t-popup-animation-expand-in-bottom,
    0.2s linear 0s 1 normal none running t-fade-in;
}

.variable-selector-popup[class~="t-popup--animation-expand-leave-active"][data-popper-placement^="bottom"] {
  animation:
    0.2s cubic-bezier(0.38, 0, 0.24, 1) 0s 1 normal none running
      t-popup-animation-expand-out-bottom,
    0.2s cubic-bezier(0, 0, 0.15, 1) 0s 1 normal none running t-fade-out;
}

// 级联选择器样式 - 使其与 cascader 保持一致，仅作用于 variable-selector-popup
.variable-selector-popup [class~="t-cascader__panel"] {
  display: flex;
  height: 100%;
  // 移除负 margin，让 popup 宽度能正确适应内容
  margin: 0;
}

.variable-selector-popup
  [class~="t-cascader__panel"][class~="t-cascader--normal"] {
  height: 200px;
}

.variable-selector-popup
  [class~="t-cascader__panel"][class~="t-cascader--multiple"] {
  height: 280px;
}

.variable-selector-popup [class~="t-cascader__menu-list"] {
  display: flex;
  flex-direction: row;
}

.variable-selector-popup [class~="t-cascader__menu"] {
  width: 148px;
  overflow: auto;
  box-sizing: content-box;
  padding: var(--td-pop-padding-m);
  background: var(--td-bg-color-container);
  margin: 0;
}

.variable-selector-popup [class~="t-cascader__menu"][class~="t-size-l"] {
  padding: var(--td-pop-padding-l);
}

.variable-selector-popup [class~="t-cascader__menu"][class~="t-size-s"] {
  padding: var(--td-pop-padding-s);
}

// 修复 narrow-scrollbar t-cascader__menu--segment 的 margin 过大问题
.variable-selector-popup [class~="t-cascader__menu--segment"] {
  border-right: 1px solid var(--td-component-stroke);
  margin: 0;
}

.variable-selector-popup [class~="t-cascader__menu--filter"] {
  width: auto;
  min-width: 148px;
}

// 级联选择器菜单项样式 - 使其与 cascader 保持一致
.variable-selector-popup [class~="t-cascader__item"] {
  position: relative;
  display: flex;
  align-items: center;
  height: var(--td-comp-size-s);
  font: var(--td-font-body-medium);
  color: var(--td-text-color-primary);
  padding: 0 var(--td-comp-paddingLR-s);
  border-radius: var(--td-radius-default);
  margin-top: var(--td-comp-margin-xxs);
  transition: background-color 0.2s cubic-bezier(0.82, 0, 1, 0.9);
  list-style: none;
  --ripple-color: var(--td-bg-color-container-active);
}

.variable-selector-popup [class~="t-cascader__item"][class~="t-size-s"] {
  height: var(--td-comp-size-xs);
  font: var(--td-font-body-small);
  padding: 0px var(--td-comp-paddingLR-s);
}

.variable-selector-popup [class~="t-cascader__item"][class~="t-size-l"] {
  height: var(--td-comp-size-m);
  font: var(--td-font-body-large);
  padding: 0 var(--td-comp-paddingLR-m);
}

.variable-selector-popup [class~="t-cascader__item"][class~="t-is-disabled"] {
  color: var(--td-text-color-disabled);
  background-color: transparent;
  cursor: not-allowed;
}

.variable-selector-popup [class~="t-cascader__item-icon"][class~="t-icon"] {
  position: absolute;
  height: 100%;
  right: 0px;
  top: 0px;
  background: transparent;
  margin: 0px 8px;
  font-size: 16px;
  color: var(--td-text-color-placeholder);
}

.variable-selector-popup [class~="t-cascader__item-icon"][class~="t-loading"] {
  display: flex;
  color: var(--td-text-color-brand);
}

.variable-selector-popup [class~="t-cascader__item"][class~="t-is-expanded"] {
  background: var(--td-brand-color-light);
  color: var(--td-text-color-brand);
}

.variable-selector-popup
  [class~="t-cascader__item"][class~="t-is-expanded"]
  [class~="t-icon-chevron-right"] {
  color: var(--td-text-color-brand);
}

.variable-selector-popup
  [class~="t-cascader__item"][class~="t-is-expanded"]:hover {
  cursor: pointer;
}

.variable-selector-popup [class~="t-cascader__item"]:first-child {
  margin-top: 0px;
}

.variable-selector-popup [class~="t-cascader__item--with-icon"] {
  padding: 0 calc(var(--td-comp-paddingLR-s) + 14px) 0
    var(--td-comp-paddingLR-s);
}

.variable-selector-popup [class~="t-cascader__item-label"] {
  display: block;
  position: relative;
  white-space: nowrap;
  padding: 0px;
}

.variable-selector-popup [class~="t-cascader__item-label--ellipsis"] {
  white-space: nowrap;
  overflow-wrap: normal;
  overflow: hidden;
  text-overflow: ellipsis;
}

.variable-selector-popup [class~="t-cascader__item-label--filter"] {
  color: var(--td-text-color-brand);
}

.variable-selector-popup
  [class~="t-cascader__item"]:hover:not([class~="t-is-expanded"]):not(
    [class~="t-is-disabled"]
  ) {
  background: var(--td-bg-color-container-hover);
  cursor: pointer;
}

.variable-selector-popup [class~="t-cascader__item"][class~="t-is-selected"] {
  color: var(--td-brand-color);
  background: var(--td-brand-color-light);
}

.variable-selector-popup
  [class~="t-cascader__item"][class~="t-is-selected"]
  [class~="t-checkbox__label"] {
  color: var(--td-brand-color);
}
</style>
